<template>
  <GChart
    type="ColumnChart"
    :data="chartData"
    :options="chartOptions"
    :resizeDebounce="500"
  />
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { GChart } from 'vue-google-charts/legacy';
import { useTrace } from '@/use';

const trace = useTrace();

export default {
  name: 'GraphJobListing',
  props: {
    jobListingId: {
      type: String,
      default: 'jobListing',
    },
  },
  data() {
    return {
      jobListingCandidates: [],
    };
  },
  components: {
    GChart,
  },
  computed: {
    ...mapGetters('stages', ['orderedStages']),
    activeCandidates() {
      return this.jobListingCandidates.filter((jobListingCandidate) => !jobListingCandidate.isStop);
    },
    stoppedCandidates() {
      return this.jobListingCandidates.filter((jobListingCandidate) => jobListingCandidate.isStop);
    },
    chartData() {
      const data = [['Stage', 'Amount', { role: 'annotation' }, { role: 'style' }]];
      this.orderedStages.forEach((stage) => {
        const amount = this.activeCandidates.filter(
          (jobListingCandidate) => jobListingCandidate.stageId === stage.id,
        ).length;
        data.push([stage.name, amount, amount.toString(), '#1e0ca9']);
      });
      data.push(['Stopped', this.stoppedCandidates.length, this.stoppedCandidates.length.toString(), '#1e0ca9']);
      return data;
    },
    chartOptions() {
      return {
        title: `Total Candidates (${this.jobListingCandidates.length})`,
        vAxis: {
          minValue: this.maxSeries,
          title: '',
        },
        width: '100%',
        legend: 'none',
        annotations: {
          alwaysOutside: true, // This ensures labels are always outside the columns (optional)
          textStyle: {
            fontSize: 12, // You can adjust the font size
            auraColor: 'none', // Removes the background color around the text
            color: '#555', // You can set the text color
          },
          // This is necessary to actually show the annotations
          stem: {
            length: 0,
          },
        },
        // Enable the annotations for the series you want, assuming it's the first series (index 0)
        series: {
          0: {
            annotations: {
              textStyle: {
                fontSize: 12,
                color: '#000',
              },
            },
          },
        },
      };
    },
    maxSeries() {
      if (this.chartData?.length > 1) {
        const amounts = this.chartData
          .map((data) => data[1])
          .slice(1);
        const highestAmount = Math.max(...amounts);
        return (highestAmount + 1);
      }
      return 100;
    },
  },
  watch: {
    jobListingId() {
      this.refresh();
    },
  },
  destroyed() {
    this.$root.$off('update:jobListingCandidate', this.refresh);
  },
  async mounted() {
    await this.refresh();
    this.$root.$on('update:jobListingCandidate', this.refresh);
  },
  methods: {
    ...mapActions('jobListingCandidates', ['LOAD_jobListingCandidatesByJobListingIdForGraphs']),
    async refresh() {
      const span = trace.startSpan('GraphJobListing.refresh');
      try {
        const { jobListingId } = this;
        // eslint-disable-next-line vue/max-len
        this.jobListingCandidates = await this.LOAD_jobListingCandidatesByJobListingIdForGraphs(jobListingId);
        span.setAttribute('candidates', this.jobListingCandidates.length);
      } catch (error) {
        span.setStatus({ code: 2, message: 'Error loading job listing graph' });
        span.recordException(error);
        this.$root.$emit('toast:error', 'Error loading job listing graph');
      } finally {
        span.end();
      }
    },
  },
};
</script>
